import * as React from 'react';

import AuthContext from "../context/AuthContext";

import { useNavigate, Navigate } from 'react-router-dom';

import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import AuthLayout, { primaryButtonSx } from './auth/AuthLayout';

import useAxios from "../utils/useAxios";

// Kinds of business (Receipt-Web-Server management/features.py): each turns on the modules it usually needs; all of them
// can be changed later in Configuración › Tu negocio.
const BUSINESS_TYPES = [
    { key: 'cafeteria', label: 'Cafetería o restaurante' },
    { key: 'tienda', label: 'Tienda o minisúper' },
    { key: 'encargos', label: 'Compras por encargo' },
    { key: 'servicios', label: 'Servicios' },
    { key: 'otro', label: 'Otro' },
];

const CreateOrganization = () => {
    const navigate = useNavigate();
    const axios = useAxios();

    const { user } = React.useContext(AuthContext);

    const [organization, setOrganization] = React.useState<string>('');
    const [businessType, setBusinessType] = React.useState<string>('');
    const [error, setError] = React.useState<string>('');
    const [submitting, setSubmitting] = React.useState<boolean>(false);

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    const createOrganization = async () => {
        if (!organization.trim()) {
            setError('Ingresa el nombre de tu organización.');
            return;
        }
        setError('');
        setSubmitting(true);
        try {
            await axios.post('management/organization/', {
                name: organization.trim()
            });
            // Optional: the panel then shows only what that kind of business uses (an older server just ignores it).
            if (businessType) await axios.put('management/organization/features/', { business_type: businessType }).catch(() => {});
            navigate('/admin', { replace: true })
        } catch (err: any) {
            // Server returns either a plain string or DRF field errors ({ name: [...] }).
            const data = err?.response?.data;
            const message = typeof data === 'string'
                ? data
                : data?.name?.[0] ?? 'No se pudo crear la organización. Intenta de nuevo.';
            setError(message);
            setSubmitting(false);
        }
    }


    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        createOrganization();
    }

    return (
        <AuthLayout
            title="Tu organización"
            subtitle="Dale nombre a tu organización y dinos qué tipo de negocio es: te mostramos solo lo que usas. Si buscas unirte a una organización existente, solicita al administrador que te invite."
        >
            <Box component="form" onSubmit={handleSubmit}>
                <TextField
                    required
                    id="name"
                    name="name"
                    label="Nombre"
                    fullWidth
                    size="small"
                    autoFocus
                    autoComplete="organization"
                    error={!!error}
                    sx={{ mb: error ? 2 : 3 }}
                    onChange={(e) => { setOrganization(e.target.value); setError(''); }}
                />
                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>¿Qué tipo de negocio es? <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>(opcional)</Box></Typography>
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 3 }}>
                    {BUSINESS_TYPES.map(type => (
                        <Chip key={type.key} label={type.label} data-testid={`business-type-${type.key}`}
                            color={businessType === type.key ? 'primary' : 'default'} variant={businessType === type.key ? 'filled' : 'outlined'}
                            onClick={() => setBusinessType(current => (current === type.key ? '' : type.key))} />
                    ))}
                </Stack>
                <Button fullWidth variant="contained" type="submit" disabled={submitting} sx={primaryButtonSx}>
                    {submitting ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Crear'}
                </Button>
            </Box>
        </AuthLayout>
    )
}

export default CreateOrganization;
